import type { GetToken } from "./canvasService";
import { apiUrl } from "@/config/api";
import { tracedFetch } from "@/utils/tracedFetch";

export type PageCategory = "question" | "info" | "deep-dive" | "critical";

export interface NodePage {
  id: string;
  node_id: string;
  org_id: string;
  // Nests this page as a sub-subject under another page. null/undefined = a
  // top-level page. The API returns a flat list; buildPageTree reconstructs it.
  parent_page_id?: string | null;
  title: string;
  category: PageCategory;
  content: string;
  summary: string;      // short one-line blurb for the page card
  code: string;         // optional code snippet shown on the card
  asks: string[];       // "You asked" — questions the learner raised
  created_at: string;
  updated_at: string;
}

// A top-level page with its one level of sub-subjects attached.
export interface NodePageWithChildren extends NodePage {
  children: NodePage[];
}

// buildPageTree reconstructs the one-level sub-subject tree from the flat list
// the API returns: top-level pages (no parent, or a parent that isn't present)
// carry their children — the pages whose parent_page_id points at them. Input
// order is preserved for both parents and children; nothing is dropped.
export function buildPageTree(pages: NodePage[]): NodePageWithChildren[] {
  const byId = new Set(pages.map((p) => p.id));
  const isTopLevel = (p: NodePage) => !p.parent_page_id || !byId.has(p.parent_page_id);
  const tops = pages.filter(isTopLevel);
  return tops.map((p) => ({
    ...p,
    children: pages.filter((c) => !isTopLevel(c) && c.parent_page_id === p.id),
  }));
}

// dropPageFromTree removes a page by id whether it sits at the top level (its
// children go with it) or is nested under a parent. Returns a new tree; the
// input is left untouched.
export function dropPageFromTree(
  tree: NodePageWithChildren[],
  id: string,
): NodePageWithChildren[] {
  return tree
    .filter((p) => p.id !== id)
    .map((p) => ({ ...p, children: p.children.filter(childIsNot(id)) }));
}

const childIsNot = (id: string) => (c: NodePage) => c.id !== id;

async function authedFetch(url: string, getToken: GetToken, init?: RequestInit): Promise<Response> {
  const token = await getToken();
  const res = await tracedFetch(apiUrl(url), {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) {
    const body = await res.text().catch(() => res.statusText);
    throw new Error(`${res.status}: ${body}`);
  }
  return res;
}

export async function listNodePages(getToken: GetToken, nodeId: string): Promise<NodePage[]> {
  const res = await authedFetch(`/api/nodes/${nodeId}/pages`, getToken);
  return res.json() as Promise<NodePage[]>;
}

export async function listForgePages(getToken: GetToken, forgeId: string): Promise<NodePage[]> {
  const res = await authedFetch(`/api/forges/${forgeId}/pages`, getToken);
  return res.json() as Promise<NodePage[]>;
}

// Optional rich fields for a page. Backend update is COALESCE (partial), so only
// the keys you actually pass are written — omit one to leave it untouched.
export interface NodePageRich {
  summary?: string;
  code?: string;
  asks?: string[];
  parent_page_id?: string | null;
}

export async function createNodePage(
  getToken: GetToken,
  nodeId: string,
  title: string,
  category: PageCategory,
  content: string,
  rich?: NodePageRich,
): Promise<NodePage> {
  const res = await authedFetch(`/api/nodes/${nodeId}/pages`, getToken, {
    method: "POST",
    body: JSON.stringify({ title, category, content, ...(rich ?? {}) }),
  });
  return res.json() as Promise<NodePage>;
}

export async function updateNodePage(
  getToken: GetToken,
  pageId: string,
  title: string,
  category: PageCategory,
  content: string,
  rich?: NodePageRich,
): Promise<void> {
  await authedFetch(`/api/pages/${pageId}`, getToken, {
    method: "PUT",
    body: JSON.stringify({ title, category, content, ...(rich ?? {}) }),
  });
}

export async function deleteNodePage(getToken: GetToken, pageId: string): Promise<void> {
  await authedFetch(`/api/pages/${pageId}`, getToken, { method: "DELETE" });
}
